export const metadata = {
  title: "Jonty Tejani — Software Engineer",
  description: "Software engineer with 5+ years building distributed systems, platform infrastructure, and customer-facing products, mostly in Go. Currently building LLM and messaging infrastructure at Dialogica.",
  name: "Jonty Tejani",
  role: "Software Engineer"
};

export const navItems = [
  { label: "About", href: "#about" },
  { label: "Experience", href: "#experience" },
  { label: "Projects", href: "#projects" },
  { label: "Skills", href: "#skills" },
  { label: "Contact", href: "#contact" }
];

export const proofPoints = [
  "5+ years",
  "Go + Node + Python",
  "NATS JetStream + PostgreSQL",
  "OpenAI + Claude + Gemini",
  "React + Remix"
];

export const gridItems = [
  {
    title: "Reliable Distributed Systems",
    description: "Designing Go services that stay correct when things fail: exactly-once delivery, per-recipient ordering, idempotent retries, and multi-tenant isolation on Postgres and NATS JetStream."
  },
  {
    title: "AI & LLM Infrastructure",
    description: "Building the plumbing behind AI products, from a multi-provider LLM gateway across OpenAI, Claude, and Gemini that fails over streaming requests before any output reaches the user, to MCP servers that connect models to real data."
  },
  {
    title: "Product-Facing Full Stack",
    description: "Shipping customer-facing features end to end, from Shopify commerce backends to Remix and React storefronts serving 50K+ student-athletes, and turning ambiguous requirements into production systems."
  }
];

export const workExperience = [
  {
    id: 1,
    title: "Go Backend Engineer",
    company: "Dialogica",
    link: "https://dialogicaai.com/",
    duration: "Jun 2026 - Present",
    summary: "Building LLM and messaging infrastructure in Go for Dia, Dialogica's AI assistant for legal professionals",
    highlights: [
      "Built a multi-provider LLM gateway across OpenAI, Claude, and Gemini that routes around provider failures, with a first-token commit boundary so streaming requests can fail over before any output reaches the client",
      "Designed a multi-tenant notification platform in Go with exactly-once delivery and per-recipient ordering through worker crashes, broker redeliveries, and retries, using layered idempotency across Postgres and NATS JetStream, now used by multiple downstream services",
      "Fixed message reordering during failure recovery by partitioning recipients onto dedicated JetStream subjects and retrying in place, then validated the fix with integration tests against a live broker",
      "Caught three release-blocking defects before launch (a cross-tenant data isolation issue, a billing validation vulnerability, and a provider outage masked by SSE parsing) and added regression coverage for each"
    ]
  },
  {
    id: 2,
    title: "Software Engineer",
    company: "Influxer",
    link: "https://influxermerch.com/",
    duration: "Jun 2024 - Jun 2026",
    summary: "Commerce backend and storefront for a merch platform serving 50K+ student-athletes",
    highlights: [
      "Re-architected the Shopify commerce backend into Go microservices and Python services, cutting checkout latency from 900 ms to 250 ms across 500K+ SKUs under promotional traffic",
      "Built an event-driven order processing system on NATS streams with durable consumers, handling 5K+ daily orders at 99.9% uptime and cutting processing latency from 5 s to 1 s",
      "Led the company website redesign with Remix, React, and the Shopify Storefront API, reducing page load times by 40% and increasing session duration by 25%",
      "Architected a Go email notification system on the Klaviyo API for merchandise approvals, abandoned carts, and sign-up reminders, driving a 23% increase in conversions across 10K+ weekly transactions",
      "Built internal analytics dashboards in Go on optimized SQL and NATS streams, cutting query response times by over 70%"
    ]
  },
  {
    id: 3,
    title: "Full Stack Engineer",
    company: "Pro-Tek Consulting",
    duration: "Sep 2023 - Jun 2024",
    summary: "APIs and delivery pipelines for pharmaceutical education platforms",
    highlights: [
      "Developed REST and GraphQL APIs in Go for pharmaceutical education platforms supporting 10K+ concurrent users at sub-200 ms p95 latency",
      "Redesigned MongoDB indexing and rewrote inefficient queries, cutting query execution time by 70%",
      "Rebuilt CI/CD pipelines on AWS, reducing deployment time from 2 hours to 15 minutes and preventing 85% of regressions through automated test enforcement",
      "Built a reusable Node.js pagination module adopted across multiple services, reducing database load by 25%"
    ]
  },
  {
    id: 4,
    title: "Software Engineer",
    company: "Par Solution",
    duration: "Oct 2018 - Jul 2021",
    summary: "Backend and mobile features for a fitness platform with 1M+ downloads",
    highlights: [
      "Contributed core workout-plan, content-streaming, and subscription features with Node.js and React Native, helping the app reach 1M+ downloads",
      "Refactored a monolithic Express.js architecture, improving request throughput by 45% while staying backward compatible for 100K+ active users",
      "Expanded test coverage from 0% to 50% with Jest and Mocha, reducing production regressions by 40%",
      "Delivered a Django + MySQL web application in Agile sprints"
    ]
  }
];

export const projects = [
  {
    id: 1,
    title: "Collab Tasks",
    description: "Real-time collaborative task manager with a Go API and an ordered event log per project",
    image: "/api/placeholder/600/400",
    tech: ["Go", "PostgreSQL", "Redis", "Next.js", "WebSockets"],
    featured: true,
    details: "Every write runs in one Postgres transaction that locks the project, checks workflow rules, and appends to a gapless per-project event log; client-generated change IDs make retries safe. Redis pub/sub only signals that something changed, so a lost message does no harm and a 5-second poll covers Redis outages. Load-tested with k6: 899 writes/s across 20 projects at 73 ms p95, with 0 of 83,534 requests failing; 200 live viewers received every event in order. Built with pgx, sqlc, goose, OpenAPI codegen, Testcontainers, and Playwright."
  },
  {
    id: 2,
    title: "Ticketing",
    description: "Event-driven ticket marketplace built as five microservices on Kubernetes",
    image: "/api/placeholder/600/400",
    tech: ["TypeScript", "NATS", "Kubernetes", "Node.js", "MongoDB", "Redis", "Next.js", "Stripe"],
    featured: true,
    github: "https://github.com/tjonty/ticketing",
    details: "Auth, tickets, orders, payments, and expiration services communicate over a NATS Streaming event bus, each with its own MongoDB database. The expiration service uses a Redis-backed Bull queue to release unpaid orders. Includes a Next.js client, Stripe payments, Jest and Supertest suites, Skaffold for local development, and per-service GitHub Actions workflows for testing and deployment."
  },
  {
    id: 3,
    title: "MongoDB MCP",
    description: "Model Context Protocol server that lets Claude read and write MongoDB through natural language",
    image: "/api/placeholder/600/400",
    tech: ["TypeScript", "MCP", "MongoDB"],
    featured: true,
    github: "https://github.com/tjonty/mongodb-mcp",
    demo: "https://youtu.be/yNfE1Xu9xeI?si=zfewyllaM1rg4LK8",
    details: "Exposes a MongoDB database to Claude Desktop as MCP tools for reading database info, creating collections, and querying, inserting, and updating documents, so data work happens directly in the conversation."
  },
  {
    id: 4,
    title: "go-lib",
    description: "Type-safe Go library that brings JavaScript's array helpers to Go slices",
    image: "/api/placeholder/600/400",
    tech: ["Go", "Generics", "Benchmarks"],
    featured: false,
    github: "https://github.com/tjonty/go-lib",
    details: "Implements 13 Array.prototype-style helpers for Go slices, such as Map, Filter, Reduce, Splice, FlatMap, ToSpliced, and With, built on generics with no reflection. Mutating helpers like Fill and CopyWithin match JavaScript's behavior, while ToSpliced, ToReversed, and With return new slices. Covered by unit tests and benchmarks."
  },
  {
    id: 5,
    title: "Linq Calendar Bot",
    description: "Book meetings by text over iMessage, RCS, and SMS and get a real calendar invite back",
    image: "/api/placeholder/600/400",
    tech: ["Node.js", "Express", "Webhooks"],
    featured: false,
    github: "https://github.com/tjonty/linq-calendar-bot",
    demo: "https://youtu.be/XQsEoFF3CNs",
    details: "A conversational scheduling bot on the Linq Partner API. It verifies every incoming webhook with HMAC SHA-256 and a 5-minute replay window, parses natural-language times like \"Tue 4:30pm for 15 minutes CT\" with timezone-safe UTC normalization, and replies in the thread with a generated .ics invite."
  },
  {
    id: 6,
    title: "One Point",
    description: "All your professional links on one editable page",
    image: "/api/placeholder/600/400",
    tech: ["SvelteKit", "Firebase", "Tailwind CSS"],
    featured: false,
    github: "https://github.com/tjonty/one-point",
    demo: "https://onepoint1.netlify.app/",
    details: "A streamlined SvelteKit web app that uses Firebase for authentication and data, and Tailwind CSS with DaisyUI for responsive design."
  }
];

export const skills = {
  backend: [
    "Go",
    "Gin",
    "Watermill",
    "sqlc + pgx",
    "Node.js",
    "Python",
    "REST",
    "GraphQL",
    "Microservices"
  ],
  distributed: [
    "NATS JetStream",
    "Event-Driven Systems",
    "Idempotency",
    "Message Ordering",
    "Distributed Locking",
    "Retry Semantics",
    "Fault Tolerance",
    "Multi-Tenancy"
  ],
  ai: [
    "Multi-Provider LLM Gateways",
    "LLM Failover & Routing",
    "SSE Streaming & Parsing",
    "Model Context Protocol",
    "Cursor",
    "Claude Code"
  ],
  data: [
    "PostgreSQL",
    "TimescaleDB",
    "Redis (Lua)",
    "MySQL",
    "MongoDB",
    "SQL Optimization"
  ],
  cloud: [
    "AWS",
    "Azure",
    "Docker",
    "Kubernetes",
    "GitHub Actions",
    "Trivy",
    "govulncheck",
    "goose + golang-migrate"
  ],
  frontend: [
    "React",
    "TypeScript",
    "Remix",
    "React Router",
    "TanStack Query",
    "Vite",
    "Tailwind CSS"
  ],
  securityTesting: [
    "Envelope Encryption",
    "RSA-OAEP + AES-GCM",
    "Azure Key Vault",
    "Testcontainers",
    "Go Integration Testing",
    "Jest",
    "Mocha"
  ]
};

export const socialMedia = [
  {
    name: "Email",
    url: "mailto:jontytejani@gmail.com",
    icon: "Mail"
  },
  {
    name: "LinkedIn",
    url: "https://www.linkedin.com/in/jontytejani/",
    icon: "Linkedin"
  },
  {
    name: "X",
    url: "https://x.com/tjonty23",
    icon: "Twitter"
  },
  {
    name: "GitHub",
    url: "https://github.com/tjonty",
    icon: "Github"
  }
];
